import React from 'react';
import { useQuery } from '@tanstack/react-query';
import EnhancedStatusTiles, { EnhancedStatusPayload } from '../components/EnhancedStatusTiles';
import TelemetryTiles, { TelemetryAgg, TelemetryHealth } from '../components/TelemetryTiles';
import { cpFetch, createAuthHeaders } from '../lib/cpClient';
import { useAuthStore } from '../store/authStore';
import { Activity, AlertTriangle, CheckCircle, HeartPulse } from 'lucide-react';
import { getPollMs, isPageVisible } from '../lib/perfMode';
import PageShell from '../components/PageShell';
import SharedStatsCard from '../components/StatsCard';
import StatusDot from '../components/StatusDot';
import { useSharedServiceHealth } from '../lib/ServiceHealthContext';
import { presentVolumeOrTimeout } from '../features/api-manager/ingestHonesty';

export default function EnhancedSystemHealthPage() {
  const { token, selectedTenant } = useAuthStore();

  const authHeaders = React.useMemo(() => {
    if (!token || !selectedTenant) return null;
    return createAuthHeaders(token, selectedTenant);
  }, [token, selectedTenant]);

  const { query: statusQuery, payload: statusPayload, health: serviceHealth } = useSharedServiceHealth();

  const telemetryHealthQuery = useQuery<TelemetryHealth | null>({
    queryKey: ['enhanced-system-health', 'telemetry-health', selectedTenant],
    queryFn: async () => {
      try {
        // Skip polling if tab is hidden to prevent OOM in Cursor/Electron
        if (!isPageVisible()) return null;
        return await cpFetch('/telemetry/health', { headers: authHeaders ?? {} });
      } catch (e) {
        console.error('Failed to fetch telemetry health:', e);
        return null;
      }
    },
    enabled: !!authHeaders,
    refetchInterval: getPollMs(30000),
    staleTime: 5000,
    refetchOnWindowFocus: false
  });

  const telemetryAggQuery = useQuery<TelemetryAgg | null>({
    queryKey: ['enhanced-system-health', 'telemetry-agg', selectedTenant],
    queryFn: async () => {
      if (!isPageVisible()) return null;
      return await cpFetch('/telemetry/agg?groupBy=tenant&window=1h', { headers: authHeaders ?? {} });
    },
    enabled: !!authHeaders,
    refetchInterval: getPollMs(30000),
    staleTime: 5000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const healthyServices = serviceHealth.ok;
  const degradedServices = serviceHealth.degraded;
  const downServices = serviceHealth.down;
  const telemetryVolume = presentVolumeOrTimeout({
    isLoading: telemetryAggQuery.isLoading,
    isError: telemetryAggQuery.isError,
    errorMessage: telemetryAggQuery.error ? String((telemetryAggQuery.error as Error)?.message || telemetryAggQuery.error) : null,
    total: telemetryAggQuery.data?.summary?.totalMessages,
    rate: telemetryAggQuery.data?.summary?.msgsPerMin,
  });

  const systemHealthStatus = serviceHealth.status;
  const statusVariant = serviceHealth.variant;

  if (!token || !selectedTenant) {
    return (
      <PageShell
        title="System Health"
        subtitle="Select a tenant and authenticate to view system health monitoring."
        icon={HeartPulse}
        iconColor="bg-red-500"
      >
        <div />
      </PageShell>
    );
  }

  return (
    <PageShell
      title="System Health"
      subtitle="Comprehensive health checks with diagnostic reports and service history."
      icon={HeartPulse}
      iconColor="bg-red-500"
      actions={
        <div className="flex items-center gap-3">
          <StatusDot status={statusVariant} label={serviceHealth.label} />
        </div>
      }
    >
      {/* System Metrics */}
      <section className="animate-slide-up">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
          <SharedStatsCard title="Healthy Services" count={healthyServices} subtitle="Operating normally" icon={CheckCircle} color="bg-green-600" />
          <SharedStatsCard title="Degraded Services" count={degradedServices} subtitle="Require attention" icon={AlertTriangle} color="bg-orange-500" />
          <SharedStatsCard title="Services Down" count={downServices} subtitle="Unavailable" icon={AlertTriangle} color="bg-red-600" />
          <SharedStatsCard title="Telemetry (1h)" count={telemetryVolume.count} subtitle={telemetryVolume.subtitle} icon={Activity} color="bg-indigo-600" />
        </div>
      </section>

      {/* Service Status Tiles */}
      <section className="animate-slide-up" style={{ animationDelay: '100ms' }}>
        <div className="flex items-center gap-2 mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Service Status</h2>
        </div>
        <EnhancedStatusTiles
          data={(statusPayload as EnhancedStatusPayload | null) ?? undefined}
          isLoading={statusQuery.isLoading}
          error={statusQuery.error ? String(statusQuery.error) : null}
        />
      </section>

      {/* Telemetry */}
      <section className="animate-slide-up" style={{ animationDelay: '200ms' }}>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Telemetry Health</h2>
        <TelemetryTiles
          health={telemetryHealthQuery.data ?? undefined}
          aggregate={telemetryAggQuery.data ?? undefined}
          isLoading={telemetryHealthQuery.isLoading || telemetryAggQuery.isLoading}
          error={telemetryHealthQuery.error ? String(telemetryHealthQuery.error) : telemetryAggQuery.error ? String(telemetryAggQuery.error) : null}
          sessionTenant={selectedTenant}
          showHeading={false}
        />
      </section>

      {/* Footer */}
      <section className="text-xs text-gray-500 border-t pt-4">
        <div className="flex justify-between items-center">
          <div className="flex gap-6">
            <span>Services: {statusQuery.isFetching ? 'Refreshing...' : 'Updated'}</span>
            <span>Telemetry: {telemetryHealthQuery.isFetching || telemetryAggQuery.isFetching ? 'Refreshing...' : 'Updated'}</span>
            <span>Auto-refresh: 30s</span>
          </div>
          <StatusDot status={statusVariant} label={`Monitoring ${systemHealthStatus}`} size="sm" />
        </div>
      </section>
    </PageShell>
  );
}
